import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  User, Briefcase, FileText, Search, MapPin, Clock, Building2,
  ArrowRight, ChevronDown, ChevronUp, LogOut, Upload, Edit3,
  CheckCircle, X, Loader2, DollarSign, Tag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = [
  'Administration', 'BTP / Travaux', 'Commerce / Vente', 'Comptabilite / Finance',
  'Education / Formation', 'Hotellerie / Restauration', 'Informatique / Digital',
  'Juridique', 'Logistique / Transport', 'Ressources Humaines', 'Sante',
  'Tourisme', 'Autre'
];

const CandidatDashboard = () => {
  const { user, token, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('offres');
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [offers, setOffers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [profileForm, setProfileForm] = useState({
    headline: '', sector: '', availability: 'immediat', contact_preference: 'indirect',
    skills: [], experience_years: 0, education_level: '', desired_contract: [],
    desired_location: '', summary: ''
  });
  const [skillInput, setSkillInput] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'candidat')) navigate('/espace-emploi');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profRes, appsRes, offersRes] = await Promise.all([
        axios.get(`${API}/espace-emploi/profile`, { headers }),
        axios.get(`${API}/espace-emploi/candidat/applications`, { headers }),
        axios.get(`${API}/job-offers`),
      ]);
      const p = profRes.data.profile || {};
      setProfile(p);
      setProfileForm(prev => ({ ...prev, ...p }));
      setApplications(appsRes.data);
      setOffers(offersRes.data);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/espace-emploi/candidat/profile`, profileForm, { headers });
      setProfile(data);
      toast.success('Profil mis à jour');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const uploadCv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCvUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/espace-emploi/candidat/upload-cv`, fd, { headers });
      toast.success('CV téléversé !');
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur upload'); }
    finally { setCvUploading(false); }
  };

  const applyToOffer = async (offerId) => {
    try {
      await axios.post(`${API}/espace-emploi/candidat/apply/${offerId}`, {}, { headers });
      toast.success('Candidature envoyée !');
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const addSkill = () => {
    if (skillInput.trim() && !profileForm.skills.includes(skillInput.trim())) {
      setProfileForm(f => ({ ...f, skills: [...f.skills, skillInput.trim()] }));
      setSkillInput('');
    }
  };

  const appliedOfferIds = new Set(applications.map(a => a.offer_id));

  const filteredOffers = offers.filter(o =>
    !search || o.title?.toLowerCase().includes(search.toLowerCase()) ||
    o.company?.toLowerCase().includes(search.toLowerCase()) ||
    o.location?.toLowerCase().includes(search.toLowerCase())
  );

  if (authLoading || loading) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0D9488]" /></div>
  );

  const TABS = [
    { id: 'offres', label: 'Offres', icon: Search },
    { id: 'candidatures', label: 'Mes candidatures', icon: Briefcase },
    { id: 'profil', label: 'Mon profil', icon: User },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="candidat-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0F766E] to-[#0D9488] py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div>
            <p className="text-white/60 text-sm">Espace Candidat</p>
            <h1 className="text-xl font-bold text-white">Bonjour, {user?.name}</h1>
          </div>
          <Button variant="ghost" onClick={() => { logout(); navigate('/espace-emploi'); }} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 flex gap-0">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                tab === t.id ? 'border-[#0D9488] text-[#0D9488]' : 'border-transparent text-gray-400 hover:text-gray-600'
              }`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {t.id === 'candidatures' && applications.length > 0 && (
                <span className="text-[10px] bg-[#0D9488] text-white px-1.5 py-0.5 rounded-full">{applications.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* ── TAB: OFFRES ── */}
        {tab === 'offres' && (
          <div data-testid="tab-content-offres">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher un poste, une entreprise..." className="pl-10" data-testid="search-offers" />
              </div>
              <span className="text-sm text-gray-500">{filteredOffers.length} offre(s)</span>
            </div>

            <div className="space-y-3">
              {filteredOffers.map(offer => {
                const applied = appliedOfferIds.has(offer.id);
                const isOpen = expandedId === offer.id;
                return (
                  <div key={offer.id} className={`bg-white rounded-xl border transition-all ${isOpen ? 'border-[#0D9488] shadow-md' : 'border-gray-200'}`}>
                    <button onClick={() => setExpandedId(isOpen ? null : offer.id)} className="w-full text-left p-5 flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#0D9488]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-[#0D9488]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-[#1F2937] text-sm">{offer.title}</h3>
                        <p className="text-[#0D9488] text-xs font-medium">{offer.company}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {offer.location}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {offer.contract_type}</span>
                          {offer.salary && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> {offer.salary}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {applied && <span className="text-xs bg-[#0D9488]/10 text-[#0D9488] px-2 py-0.5 rounded-full font-medium">Postulé</span>}
                        {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-gray-100 px-5 pb-5 pt-3">
                        <p className="text-sm text-gray-600 leading-relaxed mb-4">{offer.summary}</p>
                        {offer.missions?.length > 0 && (
                          <div className="mb-3">
                            <h4 className="text-xs font-bold text-gray-700 uppercase mb-2">Missions</h4>
                            <ul className="space-y-1">{offer.missions.map((m, i) => (
                              <li key={i} className="flex gap-2 text-xs text-gray-600"><ArrowRight className="w-3 h-3 text-[#0D9488] flex-shrink-0 mt-0.5" />{m}</li>
                            ))}</ul>
                          </div>
                        )}
                        {offer.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-4">{offer.tags.map(t => (
                            <span key={t} className="bg-gray-100 text-gray-500 text-[10px] px-2 py-0.5 rounded-full">{t}</span>
                          ))}</div>
                        )}
                        {!applied ? (
                          <Button onClick={() => applyToOffer(offer.id)} className="bg-[#F59E0B] hover:bg-[#D97706] text-white text-sm px-6" data-testid={`apply-${offer.id}`}>
                            Postuler <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        ) : (
                          <div className="flex items-center gap-2 text-sm text-[#0D9488]"><CheckCircle className="w-4 h-4" /> Candidature envoyée</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── TAB: CANDIDATURES ── */}
        {tab === 'candidatures' && (
          <div data-testid="tab-content-candidatures">
            <h2 className="text-lg font-bold text-[#1F2937] mb-4">Mes candidatures ({applications.length})</h2>
            {applications.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Aucune candidature pour le moment.</p>
                <Button onClick={() => setTab('offres')} variant="outline" className="mt-4 border-[#0D9488] text-[#0D9488] text-sm">
                  Voir les offres
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {applications.map(app => (
                  <div key={app.id} className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-[#1F2937] text-sm">{app.offer_title}</h3>
                      <p className="text-xs text-gray-500">{app.company}</p>
                      <p className="text-[10px] text-gray-400 mt-1">Postulé le {new Date(app.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                      app.status === 'new' ? 'bg-blue-50 text-blue-600' :
                      app.status === 'viewed' ? 'bg-amber-50 text-amber-600' :
                      app.status === 'accepted' ? 'bg-green-50 text-green-600' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {app.status === 'new' ? 'Envoyee' : app.status === 'viewed' ? 'Consultee' : app.status === 'accepted' ? 'Retenue' : app.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: PROFIL ── */}
        {tab === 'profil' && (
          <div className="max-w-2xl" data-testid="tab-content-profil">
            <h2 className="text-lg font-bold text-[#1F2937] mb-6">Mon profil candidat</h2>

            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Titre professionnel</Label>
                <Input value={profileForm.headline || ''} onChange={e => setProfileForm(f => ({ ...f, headline: e.target.value }))}
                  placeholder="Ex: Comptable senior" className="mt-1" data-testid="profile-headline" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Secteur</Label>
                  <Select value={profileForm.sector || ''} onValueChange={v => setProfileForm(f => ({ ...f, sector: v }))}>
                    <SelectTrigger className="mt-1" data-testid="profile-sector"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Disponibilité</Label>
                  <Select value={profileForm.availability || 'immediat'} onValueChange={v => setProfileForm(f => ({ ...f, availability: v }))}>
                    <SelectTrigger className="mt-1" data-testid="profile-availability"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediat">Immédiat</SelectItem>
                      <SelectItem value="1mois">Sous 1 mois</SelectItem>
                      <SelectItem value="3mois">Sous 3 mois</SelectItem>
                      <SelectItem value="en_poste">En poste (ouvert aux opportunités)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Préférence de mise en relation</Label>
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  {[
                    { val: 'direct', label: 'Contact direct', desc: 'L\'employeur peut voir vos coordonnées' },
                    { val: 'indirect', label: 'Via AERH', desc: 'AERH fait le lien, vos coordonnées restent privées' }
                  ].map(opt => (
                    <button key={opt.val}
                      onClick={() => setProfileForm(f => ({ ...f, contact_preference: opt.val }))}
                      className={`text-left rounded-xl border-2 p-4 transition-all ${
                        profileForm.contact_préférence === opt.val ? 'border-[#0D9488] bg-[#0D9488]/5' : 'border-gray-200'
                      }`} data-testid={`contact-${opt.val}`}>
                      <p className="font-semibold text-sm text-[#1F2937]">{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Compétences</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={skillInput} onChange={e => setSkillInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    placeholder="Ajouter une compétence..." data-testid="skill-input" />
                  <Button type="button" onClick={addSkill} variant="outline" className="border-[#0D9488] text-[#0D9488]">+</Button>
                </div>
                {profileForm.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {profileForm.skills.map((s, i) => (
                      <span key={i} className="bg-[#0D9488]/10 text-[#0D9488] text-xs px-3 py-1 rounded-full flex items-center gap-1">
                        {s} <button onClick={() => setProfileForm(f => ({ ...f, skills: f.skills.filter((_, j) => j !== i) }))}><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Résumé / Présentation</Label>
                <textarea value={profileForm.summary || ''} onChange={e => setProfileForm(f => ({ ...f, summary: e.target.value }))}
                  rows={4} placeholder="Décrivez votre parcours en quelques lignes..."
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                  data-testid="profile-summary" />
              </div>

              {/* CV Upload */}
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">CV (PDF, DOC, DOCX)</Label>
                <div className="mt-2 flex items-center gap-4">
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf,.doc,.docx" onChange={uploadCv} className="hidden" data-testid="cv-upload" />
                    <div className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-gray-300 hover:border-[#0D9488] text-sm text-gray-600 hover:text-[#0D9488] transition-colors">
                      {cvUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {cvUploading ? 'Téléchargement...' : 'Télécharger mon CV'}
                    </div>
                  </label>
                  {profile?.cv_file && (
                    <span className="text-xs text-[#0D9488] flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" /> {profile.cv_file.filename}
                    </span>
                  )}
                  <Link to="/cv-builder" className="text-xs text-[#F59E0B] hover:underline flex items-center gap-1">
                    <Edit3 className="w-3 h-3" /> Créer un CV en ligne
                  </Link>
                </div>
              </div>

              <Button onClick={saveProfile} disabled={saving} className="bg-[#0D9488] hover:bg-[#0F766E] text-white font-semibold px-8 py-5" data-testid="save-profile">
                {saving ? 'Sauvegarde...' : 'Enregistrer mon profil'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidatDashboard;
